import { defaultCountries } from 'react-international-phone';
import { USER_SEARCH_DETAILS_STORAGE_KEY } from '@pkgs/components/utils/userSearchDetails';
import { safeLocalStorage } from '@pkgs/components/utils/storageUtils';
import {
  type InputAccessRequest,
  type InputAccessRequestPreferredLanguage,
  type InputContact,
  type InputContactPreferredLanguage,
} from '@pkgs/api';
import { type TaxonomyMarketCountryCode } from '@generated/search-api';

import { type RequestAccessFormData } from '@/components/property-expose/RequestAccessForm/RequestAccessForm.types';
import { type ContactFormData } from '@/components/property-expose/ContactForm/ContactForm.types';

import { getUTMParams } from './utmParams';

const mapMandatoryConsentData = (consentText?: string) => ({
  consent: true,
  consentText: consentText ?? '',
});

export const mapLeadFormDataToRequestData = ({
  formData,
  preferredLanguage,
  marketCountry,
}: {
  formData: ContactFormData;
  preferredLanguage: InputContactPreferredLanguage;
  marketCountry: TaxonomyMarketCountryCode;
}): InputContact => {
  const {
    street,
    phoneNumber,
    phoneNumberCountryCode,
    marketingConsent,
    marketingConsentText,
    privateOfficeConsent,
    privateOfficeConsentText,
    shopConsent,
    shopConsentText,
    financeConsent,
    financeConsentText,
    consentText,
    ...rest
  } = formData;

  const phoneNumberCode = defaultCountries.find((country) => country[1] === phoneNumberCountryCode)?.[2];

  const parsedPhoneNumber = phoneNumberCode && phoneNumber ? `+${phoneNumberCode}${phoneNumber}` : '';

  const { campaign, conversionData } = getUTMParams();

  return {
    ...rest,
    ...(street && { streetNameHouseNumber: street }),
    ...(parsedPhoneNumber && { phoneNumber: parsedPhoneNumber }),
    preferredLanguage,
    campaign,
    conversionData,
    consent: true,
    referral: mapMandatoryConsentData(consentText),
    contacting: mapMandatoryConsentData(consentText),
    marketCountry,
    marketingConsent: {
      country: Boolean(marketingConsent),
      countrySeenText: marketingConsentText,
      shop: Boolean(shopConsent),
      shopSeenText: shopConsentText,
      privateOffice: Boolean(privateOfficeConsent),
      privateOfficeSeenText: privateOfficeConsentText,
      financing: Boolean(financeConsent),
      financingSeenText: financeConsentText,
    },
  };
};

export const mapRequestAccessFormDataToRequestData = (
  formData: RequestAccessFormData,
  preferredLanguage: InputAccessRequestPreferredLanguage,
): InputAccessRequest => {
  const { consentText, ...restFormData } = formData;
  const phoneNumberCode = defaultCountries.find((country) => country[1] === formData.phoneNumberCountryCode)?.[2];

  const parsedPhoneNumber = phoneNumberCode && formData.phoneNumber ? `+${phoneNumberCode}${formData.phoneNumber}` : '';

  const { campaign, conversionData } = getUTMParams();

  return {
    ...restFormData,
    ...(parsedPhoneNumber && { phoneNumber: parsedPhoneNumber }),
    preferredLanguage,
    campaign,
    conversionData,
    consent: true,
    contacting: mapMandatoryConsentData(consentText),
    referral: mapMandatoryConsentData(consentText),
  };
};

export const buildContactFormMessage = (defaultMessage: string, searchDetailsIntro: string): string => {
  const searchDetails = safeLocalStorage.getItem(USER_SEARCH_DETAILS_STORAGE_KEY)?.trim();
  if (!searchDetails) return defaultMessage;

  return `${defaultMessage}\n\n${searchDetailsIntro} ${searchDetails}`;
};
